import { orderStatusConstants } from "constants/order";
import dayjs from "dayjs";
import { removeClearPatientFee } from "helpers/fee";
import { centsToDollars, parsePrice } from "helpers/price";

/** Match backend installment weekend rule (UTC calendar day). */
function isInstallmentDateOnWeekendUTC(
  year: number,
  month: number,
  day: number
): boolean {
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** First calendar day on or after `from` (UTC start-of-day) that is not Sat/Sun. */
function firstWeekdayOnOrAfterUTC(from: dayjs.Dayjs): dayjs.Dayjs {
  let cur = from.startOf("day");
  while (
    isInstallmentDateOnWeekendUTC(cur.year(), cur.month() + 1, cur.date())
  ) {
    cur = cur.add(1, "day");
  }
  return cur;
}

export function validateNextInstallmentDate(
  input: string,
  blockDays: "past" | "future"
) {
  const pattern = /^\d{2}\/\d{2}\/\d{4}$/;

  if (pattern.test(input)) {
    const parts = input.split("/");
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    const today = dayjs.utc().startOf("day");
    const tomorrow = today.add(1, "day");
    const selectedDate = dayjs(input).utc().startOf("day");

    if (
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31 &&
      year >= 1000 &&
      year <= 9999
    ) {
      if (blockDays === "future") {
        if (selectedDate.isBefore(tomorrow, "date")) {
          if (selectedDate.isSame(today, "date")) {
            const earliestAllowed = firstWeekdayOnOrAfterUTC(tomorrow);
            if (earliestAllowed.isSame(tomorrow, "date")) {
              return "The next installment cannot be due today. Please choose tomorrow.";
            }
            return `The next installment cannot be due today. Please choose the next available weekday (${earliestAllowed.format(
              "MM/DD/YYYY"
            )}).`;
          }
          return "Invalid Date";
        }
        if (isInstallmentDateOnWeekendUTC(year, month, day)) {
          return "Installment due dates cannot fall on a weekend. Please choose a weekday.";
        }
        return true;
      }
      if (selectedDate.isSameOrBefore(tomorrow, "date")) {
        if (isInstallmentDateOnWeekendUTC(year, month, day)) {
          return "Installment due dates cannot fall on a weekend. Please choose a weekday.";
        }
        return true;
      }
    }
  }

  return "Invalid Date";
}

export function validateMinDownPaymentFlat(
  value: number | null | undefined,
  totalPatientPortion: number | null,
  clearPatientFeePercentage?: number | null,
  status?: string
) {
  if (status === orderStatusConstants.PAID) {
    return true;
  }
  if (!value) {
    return true; // Let required validation handle empty values
  }

  // If totalPatientPortion is not available, we allow validation to pass
  if (!totalPatientPortion || totalPatientPortion === null) {
    return true;
  }

  const totalPatientPortionInDollars = centsToDollars(totalPatientPortion);

  // When clear patient fee exists for GFE orders, the stored value includes the fee
  // and the totalPatientPortion also includes the fee, so we compare exact amounts:
  // - stored value (with fee) against total patient portion (with fee)
  // But we show the subtotal (without fee) in the error message since that's what user enters
  if (value >= totalPatientPortionInDollars) {
    // Calculate subtotal for error message display
    const subtotalForDisplay = clearPatientFeePercentage
      ? removeClearPatientFee(
          totalPatientPortionInDollars,
          clearPatientFeePercentage
        )
      : totalPatientPortionInDollars;

    return `Amount must be less than ${parsePrice(subtotalForDisplay)}`;
  }

  return true;
}
